﻿import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaMicrophone } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { DictationType, DictationTypeFormData } from '../../types';

const DictationTypes: React.FC = () => {
  const { token } = useAuth();
  const [types, setTypes] = useState<DictationType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newType, setNewType] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);

  useEffect(() => {
    loadDictationTypes();
  }, [token]);

  const loadDictationTypes = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await apiCall('/letters/dictation-types/?active_only=false', {
        method: 'GET'
      }, token || undefined);
      const data = await response.json();
      setTypes(data.results || data);
    } catch (error) {
      console.error('Error loading dictation types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newType.trim() || !token) return;
    
    try {
      const response = await apiCall('/letters/dictation-types/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newType.trim(),
          is_active: true,
          display_order: types.length
        } as DictationTypeFormData)
      }, token || undefined);

      if (response.ok) {
        await loadDictationTypes();
        setNewType('');
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error adding dictation type:', error);
      alert('Failed to add dictation type. Please try again.');
    }
  };

  const handleDelete = async (type: DictationType) => {
    if (!window.confirm(`Are you sure you want to delete "${type.name}"?`) || !token) return;
    
    try {
      const response = await apiCall(`/letters/dictation-types/${type.id}/`, {
        method: 'DELETE'
      }, token || undefined);

      if (response.ok) {
        await loadDictationTypes();
      }
    } catch (error) {
      console.error('Error deleting dictation type:', error);
      alert('Failed to delete dictation type. Please try again.');
    }
  };

  const handleEdit = (type: DictationType) => {
    setEditingId(type.id);
    setEditValue(type.name);
  };

  const handleSaveEdit = async () => {
    if (!editValue.trim() || !editingId || !token) return;
    
    const typeToEdit = types.find(t => t.id === editingId);
    if (!typeToEdit) return;

    try {
      const response = await apiCall(`/letters/dictation-types/${editingId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editValue.trim()
        })
      }, token || undefined);

      if (response.ok) {
        await loadDictationTypes();
        setEditingId(null);
        setEditValue('');
      }
    } catch (error) {
      console.error('Error updating dictation type:', error);
      alert('Failed to update dictation type. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-base-content opacity-60">Loading dictation types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaMicrophone className="text-primary" />
          Dictation Types
        </h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn btn-primary gap-2"
        >
          <FaPlus />
          Add Type
        </button>
      </div>

      {/* Add New Type Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-base-200 rounded-lg">
          <div className="flex gap-2">
            <input
              type="text"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="Enter new dictation type..."
              className="input input-bordered flex-1"
              autoFocus
            />
            <button onClick={handleAdd} className="btn btn-success gap-2">
              <FaSave />
              Save
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn btn-ghost gap-2">
              <FaTimes />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Types List */}
      <div className="space-y-2">
        {types.length === 0 ? (
          <div className="text-center py-12 text-base-content opacity-60">
            <p>No dictation types found.</p>
            <p className="text-sm mt-2">Click "Add Type" to create your first dictation type.</p>
          </div>
        ) : (
          types.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-4 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
            >
              {editingId === type.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                    className="input input-bordered flex-1"
                    autoFocus
                  />
                  <button onClick={handleSaveEdit} className="btn btn-sm btn-success gap-1">
                    <FaSave />
                    Save
                  </button>
                  <button onClick={handleCancelEdit} className="btn btn-sm btn-ghost gap-1">
                    <FaTimes />
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="font-medium text-base-content">{type.name}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="btn btn-sm btn-ghost gap-1"
                    >
                      <FaEdit />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(type)}
                      className="btn btn-sm btn-ghost text-error gap-1"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-info/10 rounded-lg">
        <p className="text-sm text-base-content">
          <strong>Note:</strong> These dictation types will appear in the dropdown when creating new dictations. 
          They help categorize your medical dictations for better organization and workflow management.
        </p>
      </div>
    </div>
  );
};

export default DictationTypes;
